import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight, X } from "lucide-react";

const TOOLS = ["LeaseLens v5", "ESOP Valuation", "FinConsolidate"];
const SLOTS = ["Morning (10am–12pm)", "Afternoon (2pm–4pm)", "Evening (5pm–7pm)"];

export default function CTA() {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", day: "Either", slot: "", tools: [] });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  const toggleTool = (t) =>
    setForm((f) => ({
      ...f,
      tools: f.tools.includes(t) ? f.tools.filter((x) => x !== t) : [...f.tools, t],
    }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.tools.length) { setError("Please select at least one tool."); return; }
    setSubmitting(true);
    try {
      const r = await fetch("/api/demo_request.php", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      const d = await r.json();
      if (d.error) { setError(d.error); return; }
      setSuccess(true);
    } catch {
      setError("Network error. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const closeModal = () => { setOpen(false); setSuccess(false); setError(""); setForm({ name: "", email: "", day: "Either", slot: "", tools: [] }); };

  return (
    <>
      <section
        className="relative py-24 md:py-32 px-6 md:px-10 bg-white"
        data-testid="cta-section"
      >
        <motion.div
          initial={{ opacity: 0, y: 28 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="relative max-w-7xl mx-auto rounded-[32px] bg-[#202A36] text-white overflow-hidden p-10 md:p-16 lg:p-20"
        >
          <div
            className="absolute inset-0 opacity-[0.08] pointer-events-none"
            style={{
              backgroundImage:
                "linear-gradient(to right, #fff 1px, transparent 1px), linear-gradient(to bottom, #fff 1px, transparent 1px)",
              backgroundSize: "48px 48px",
            }}
          />
          <div className="relative grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
            <div className="lg:col-span-8">
              <div className="text-[11px] uppercase tracking-[0.28em] text-white/50 font-mono-x mb-5">
                §06 · Get started
              </div>
              <h3 className="font-display text-4xl md:text-6xl font-semibold leading-[1.03] tracking-[-0.03em]">
                Replace your spreadsheet stack.
                <br />
                <span className="text-white/50">In one afternoon.</span>
              </h3>
            </div>
            <div className="lg:col-span-4 flex flex-col gap-3">
              <button
                onClick={() => setOpen(true)}
                className="group inline-flex items-center justify-between gap-3 px-6 py-4 rounded-full bg-white text-[#202A36] font-medium hover:bg-gray-100 transition-colors"
                data-testid="cta-demo"
              >
                Book a CA-led walkthrough
                <ArrowUpRight
                  size={18}
                  className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                />
              </button>
            </div>
          </div>
        </motion.div>
      </section>

      {/* ── Demo Modal ── */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[200] flex items-center justify-center p-4"
            style={{ background: "rgba(7,11,20,0.85)", backdropFilter: "blur(8px)" }}
            onClick={(e) => e.target === e.currentTarget && closeModal()}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 16 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 16 }}
              transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
              className="relative w-full max-w-[500px] rounded-2xl overflow-hidden"
              style={{
                background: "#0f172a",
                border: "1px solid rgba(255,255,255,0.08)",
                boxShadow: "0 40px 80px rgba(0,0,0,0.6)",
              }}
            >
              {/* Header */}
              <div className="flex items-center justify-between px-6 py-5 border-b border-white/[0.07]">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.2em] text-sky-400/70 mb-1 font-mono">Book Demo</div>
                  <h2 className="text-lg font-semibold text-white tracking-tight">CA-Led Walkthrough</h2>
                </div>
                <button
                  onClick={closeModal}
                  className="h-8 w-8 rounded-full flex items-center justify-center text-slate-400 hover:text-white hover:bg-white/10 transition-colors"
                >
                  <X size={16} />
                </button>
              </div>

              {/* Body */}
              <div className="px-6 py-5 max-h-[70vh] overflow-y-auto">
                {success ? (
                  <div className="text-center py-8">
                    <div className="text-4xl mb-4">🎉</div>
                    <h3 className="text-white font-semibold text-lg mb-2">Request received!</h3>
                    <p className="text-slate-400 text-sm leading-relaxed">
                      We'll reach out to confirm your demo slot at <strong className="text-sky-400">{form.email}</strong>.
                      Available on Saturdays &amp; Sundays only.
                    </p>
                    <button
                      onClick={closeModal}
                      className="mt-6 px-6 py-2.5 rounded-full bg-sky-500 text-white text-sm font-medium hover:bg-sky-400 transition-colors"
                    >
                      Close
                    </button>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-4">
                    {/* Name */}
                    <div>
                      <label className="block text-xs text-slate-400 font-medium mb-1.5 tracking-wide">Full Name</label>
                      <input
                        type="text" required
                        value={form.name}
                        onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                        placeholder="Raghuveer Patel"
                        className="w-full px-4 py-2.5 rounded-xl text-sm text-white placeholder-slate-500 outline-none transition-colors"
                        style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.09)" }}
                        onFocus={(e) => (e.target.style.borderColor = "rgba(56,189,248,0.5)")}
                        onBlur={(e) => (e.target.style.borderColor = "rgba(255,255,255,0.09)")}
                      />
                    </div>

                    {/* Email */}
                    <div>
                      <label className="block text-xs text-slate-400 font-medium mb-1.5 tracking-wide">Email Address</label>
                      <input
                        type="email" required
                        value={form.email}
                        onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                        placeholder="you@company.com"
                        className="w-full px-4 py-2.5 rounded-xl text-sm text-white placeholder-slate-500 outline-none transition-colors"
                        style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.09)" }}
                        onFocus={(e) => (e.target.style.borderColor = "rgba(56,189,248,0.5)")}
                        onBlur={(e) => (e.target.style.borderColor = "rgba(255,255,255,0.09)")}
                      />
                    </div>

                    {/* Day */}
                    <div>
                      <label className="block text-xs text-slate-400 font-medium mb-1.5 tracking-wide">Preferred Day <span className="text-slate-500">(Sat &amp; Sun only)</span></label>
                      <div className="grid grid-cols-3 gap-2">
                        {["Saturday", "Sunday", "Either"].map((d) => (
                          <button
                            key={d} type="button"
                            onClick={() => setForm((f) => ({ ...f, day: d }))}
                            className="py-2 rounded-xl text-xs font-medium transition-all"
                            style={{
                              background: form.day === d ? "rgba(56,189,248,0.15)" : "rgba(255,255,255,0.04)",
                              border: form.day === d ? "1px solid rgba(56,189,248,0.4)" : "1px solid rgba(255,255,255,0.08)",
                              color: form.day === d ? "#38bdf8" : "#94a3b8",
                            }}
                          >
                            {d}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Slot */}
                    <div>
                      <label className="block text-xs text-slate-400 font-medium mb-1.5 tracking-wide">Preferred Time Slot</label>
                      <div className="grid grid-cols-1 gap-2">
                        {SLOTS.map((s) => (
                          <button
                            key={s} type="button"
                            onClick={() => setForm((f) => ({ ...f, slot: s }))}
                            className="px-4 py-2 rounded-xl text-xs font-medium text-left transition-all"
                            style={{
                              background: form.slot === s ? "rgba(56,189,248,0.12)" : "rgba(255,255,255,0.03)",
                              border: form.slot === s ? "1px solid rgba(56,189,248,0.35)" : "1px solid rgba(255,255,255,0.07)",
                              color: form.slot === s ? "#38bdf8" : "#94a3b8",
                            }}
                          >
                            {s}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Tools */}
                    <div>
                      <label className="block text-xs text-slate-400 font-medium mb-1.5 tracking-wide">Which tool(s) would you like a demo of?</label>
                      <div className="grid grid-cols-1 gap-2">
                        {TOOLS.map((t) => (
                          <button
                            key={t} type="button"
                            onClick={() => toggleTool(t)}
                            className="px-4 py-2.5 rounded-xl text-xs font-medium text-left flex items-center gap-2 transition-all"
                            style={{
                              background: form.tools.includes(t) ? "rgba(16,185,129,0.1)" : "rgba(255,255,255,0.03)",
                              border: form.tools.includes(t) ? "1px solid rgba(16,185,129,0.3)" : "1px solid rgba(255,255,255,0.07)",
                              color: form.tools.includes(t) ? "#10b981" : "#94a3b8",
                            }}
                          >
                            <span style={{ fontSize: 14 }}>{form.tools.includes(t) ? "✓" : "○"}</span>
                            {t}
                          </button>
                        ))}
                      </div>
                    </div>

                    {error && <p className="text-red-400 text-xs">{error}</p>}

                    <button
                      type="submit" disabled={submitting}
                      className="w-full py-3 rounded-xl text-sm font-semibold text-white transition-opacity"
                      style={{ background: "linear-gradient(135deg, #38bdf8, #0ea5e9)", opacity: submitting ? 0.6 : 1 }}
                    >
                      {submitting ? "Submitting…" : "Request Demo →"}
                    </button>
                    <p className="text-center text-[11px] text-slate-500">
                      We'll confirm your slot via email. Demos available Saturdays &amp; Sundays only.
                    </p>
                  </form>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
